import {
    AccountBalanceWalletOutlined as AccountBalanceWalletOutlinedIcon,
    ExpandMoreRounded as ExpandMoreRoundedIcon,
} from 'components/icons';
import { cn } from 'utils/cn';
import { TableRowCallForActionButton } from 'components/buttons/CallForActionButton.tsx';
import PermissionButton from 'components/buttons/PermissionButton';
import Loader from 'components/Loader';
import { useModal } from 'components/modals/useModal';
import { FORM_OVERFLOW_CUSTOM_STYLE } from 'components/modals/util.tsx';
import SubRowTable from 'components/tables/SubRowTable';
import type { DataTableColumnDef } from 'components/tables/data-table';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import NestedTable from 'components/tables/list-table/NestedTable';
import { FilterConfig } from 'components/tables/list-table/ListTableTypes';
import ListTableWithFilters from 'components/tables/list-table/ListTableWithFilters';
import React, { useCallback, useMemo } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    useCreateCompanyBankAccountMutation,
    useLazyGetCompaniesWithFiltersQuery,
    useUpdateCompanyBankAccountMutation,
} from 'store/api/companies';
import { useAddFinancialConditionMutation, useUpdateFinancialConditionMutation } from 'store/api/financial-conditions';
import { Company, CompanyIbanDto } from 'types/companies';
import {
    FinancialCondition,
    FinancialConditionCreateDto,
    FinancialConditionScopeType,
} from 'types/financial-conditions';
import { errorAlert } from 'utils/alert';
import { getOptionsFromClassifier } from 'utils/formUtil.ts';
import { getClassifierText, t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useClassifiers } from 'utils/useClassifiers';
import CompanyIbanEditModal from './CompanyIbanEditModal.tsx';
import FinancialConditionEditModal from './FinancialConditionEditModal';

interface CompanySubRow {
    company: Omit<Company, 'country_id'>;
}

/** The new design's column for a detail that is one long word, an IBAN or an e-mail address: 23 characters fit. */
const LONG_WORD_COLUMN = '15rem';

const FinancialConditionsTable: React.FC = () => {
    const { classifiers, isLoading: isLoadingClassifiers } = useClassifiers({
        codes: ['INVOICING_INTERVAL_TYPE', 'PRICING_LIMITATION_TYPE', 'CURRENCY', 'CONTRACT_TYPE', 'COUNTRY'],
    });
    const canUpdateFinancials = useHasPermissions(['UPDATE.FINANCIAL_CONDITIONS']);
    const isNewDesign = useIsNewDesign();
    const { openModal, closeModal } = useModal();

    const [fetchData, { data, isFetching }] = useLazyGetCompaniesWithFiltersQuery();
    const [addFinancialCondition] = useAddFinancialConditionMutation();
    const [updateFinancialCondition] = useUpdateFinancialConditionMutation();
    const [createBankAccount] = useCreateCompanyBankAccountMutation();
    const [updateBankAccount] = useUpdateCompanyBankAccountMutation();

    const onFormSumbit = useCallback(
        async (formData: FinancialConditionCreateDto, id?: string): Promise<void> => {
            try {
                if (id) {
                    updateFinancialCondition({ id, ...formData })
                        .unwrap()
                        .then(() => {
                            closeModal();
                            fetchData({});
                        })
                        .catch(console.error);
                } else {
                    addFinancialCondition({ ...formData })
                        .unwrap()
                        .then(() => {
                            closeModal();
                            fetchData({});
                        })
                        .catch(console.error);
                }
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [addFinancialCondition, closeModal, fetchData, updateFinancialCondition]
    );

    const openEditFinancialConditionModal = useCallback(
        (editableEntity: FinancialCondition | undefined, company: Company): void => {
            openModal(
                <FinancialConditionEditModal
                    financialCondition={editableEntity}
                    scopeType={FinancialConditionScopeType.COMPANY}
                    company={company}
                    onChange={(data) => {
                        void onFormSumbit(
                            data,
                            editableEntity?.id !== undefined ? String(editableEntity?.id) : undefined
                        );
                    }}
                    onClose={closeModal}
                    classifiers={classifiers}
                    includeCompanyFields={true}
                />,
                closeModal
            );
        },
        [onFormSumbit, openModal, closeModal, classifiers]
    );

    const handleOnBankAccountSubmit = useCallback(
        async (companyId: number, action: 'add' | 'update', dto: CompanyIbanDto) => {
            try {
                if (action === 'add') {
                    await createBankAccount({ companyId, dto }).unwrap();
                } else if (action === 'update' && dto.id) {
                    await updateBankAccount({ companyId, ibanId: dto.id, dto }).unwrap();
                }
                closeModal();
                fetchData({});
            } catch (e) {
                console.error('onSubmit error: ', e);
                errorAlert(t('forms.error-submitting-the-form'));
            }
        },
        [createBankAccount, updateBankAccount, closeModal, fetchData]
    );

    const openCompanyIbanEditModal = useCallback(
        (companyId: number, action: 'add' | 'update', companyIban?: CompanyIbanDto) => {
            openModal(
                <CompanyIbanEditModal
                    onClose={closeModal}
                    onSubmit={(dto) => {
                        void handleOnBankAccountSubmit(companyId, action, dto);
                    }}
                    titleId={`financial-conditions.company.${action}-bank-account`}
                    companyIban={companyIban}
                    classifiers={classifiers}
                    initialData={{
                        id: companyIban?.id || null,
                        iban: companyIban?.iban || '',
                        currencyType:
                            (
                                getOptionsFromClassifier(classifiers['CURRENCY']) as {
                                    label: string;
                                    value: string;
                                }[]
                            ).find((val) => val.value === companyIban?.currencyTypeCode) ?? null,
                        ibanReceiverName: companyIban?.ibanReceiverName || '',
                        bankName: companyIban?.bankName || '',
                        bankCode: companyIban?.bankCode || '',
                        bankSwift: companyIban?.bankSwift || '',
                        bankAddress: companyIban?.bankAddress || null,
                        mainIban: companyIban?.mainIban ?? false,
                    }}
                />,
                closeModal,
                FORM_OVERFLOW_CUSTOM_STYLE
            );
        },
        [openModal, closeModal, handleOnBankAccountSubmit, classifiers]
    );

    // Weights measured at 2560/1680/1366: the company column runs ~1.55x the six (equal) condition
    // columns.
    const columns = useMemo<DataTableColumnDef<Company>[]>(
        () => [
            {
                header: t('company'),
                accessorKey: 'name',
                enableSorting: false,
                meta: {
                    widthWeight: 1.55,
                    // The cell is nowrap, and a managed cell clips instead of bleeding into its
                    // neighbour, so this floors on the longest company name rather than on the weight.
                    minPx: 260,
                },
                cell: ({ cell }) => {
                    const { name } = cell.row.original;
                    return <div className="flex flex-col font-semibold whitespace-nowrap">{name ?? '-'}</div>;
                },
            },
            {
                header: t('financial-conditions.payment-deadline'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'paymentDeadlineInDays',
                accessorFn: (row) => row.financialCondition?.paymentDeadlineInDays,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    const value = getValue<number | undefined>();
                    return value ? value : '-';
                },
            },
            {
                header: t('financial-conditions.prepayment-payment-deadline'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'prepaymentPaymentDeadlineInDays',
                accessorFn: (row) => row.financialCondition?.prepaymentPaymentDeadlineInDays,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    const value = getValue<number | undefined>();
                    return value ? value : '-';
                },
            },
            {
                header: t('financial-conditions.invoicing-interval'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'invoicingIntervalTypeCode',
                accessorFn: (row) => row.financialCondition?.invoicingIntervalTypeCode,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    const value = getValue<string | undefined>();
                    return value ? getClassifierText(classifiers?.['INVOICING_INTERVAL_TYPE'], value) : '-';
                },
            },
            {
                header: t('financial-conditions.interest-rate'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'interestRatePct',
                accessorFn: (row) => row.financialCondition?.interestRatePct,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    const value = getValue<string | undefined>();
                    return value ? value : '-';
                },
            },
            {
                header: t('financial-conditions.interest-rate-b2c'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'interestRateB2cPct',
                accessorFn: (row) => row.financialCondition?.interestRateB2cPct,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    const value = getValue<string | undefined>();
                    return value ? value : '-';
                },
            },
            {
                header: t('currency'),
                // Explicit id: the accessor is a function, so without it the managed layout would
                // key this column's persisted geometry on the TRANSLATED header text.
                id: 'currencyTypeCode',
                accessorFn: (row) => row.financialCondition?.currencyTypeCode,
                enableSorting: false,
                meta: {
                    widthWeight: 1,
                    minPx: 100,
                },
                cell: ({ getValue }) => {
                    const value = getValue<string | undefined>();
                    return value ? getClassifierText(classifiers?.['CURRENCY'], value) : '-';
                },
            },
            {
                header: '',
                id: 'editButton',
                enableSorting: false,
                meta: {
                    // 37px "Edit" button + the managed cell's 32px horizontal padding.
                    fixedPx: 72,
                },
                cell: ({ row }) => {
                    const { financialCondition, ...rest } = row.original;
                    const editButtonPermissions = !financialCondition
                        ? ['CREATE.FINANCIAL_CONDITIONS']
                        : ['UPDATE.FINANCIAL_CONDITIONS'];
                    return (
                        <div className="flex flex-nowrap items-center justify-end">
                            <PermissionButton
                                permissions={editButtonPermissions}
                                variant="outlined"
                                size="extraSmall"
                                onClick={() => {
                                    openEditFinancialConditionModal(financialCondition ?? undefined, { ...rest });
                                }}
                            >
                                <FormattedMessage id="edit" />
                            </PermissionButton>
                        </div>
                    );
                },
            },
            {
                id: 'expander',
                enableSorting: false,
                meta: {
                    // 32px toggle button + the managed cell's 32px horizontal padding.
                    fixedPx: 64,
                },
                cell: ({ row }) => (
                    <TableRowCallForActionButton
                        onClick={row.getToggleExpandedHandler()}
                        style={{ cursor: 'pointer' }}
                        aria-label={t(row.getIsExpanded() ? 'collapse-row' : 'expand-row')}
                        shape="rounded"
                    >
                        <ExpandMoreRoundedIcon
                            className={cn(
                                'new:motion-safe:transition-transform new:duration-(--duration-fast) new:ease-(--ease-smooth-out)',
                                row.getIsExpanded() && '-scale-y-100'
                            )}
                        />
                    </TableRowCallForActionButton>
                ),
            },
        ],
        [classifiers, openEditFinancialConditionModal]
    );

    const filterConfig: FilterConfig = useMemo(
        () => ({
            quickFilters: [],
            filters: [],
            searchBarSelectors: [{ selector: 'name' }],
        }),
        []
    );

    // A company's details in reading order, each its label and its value: the classic sub-row stacks them in
    // pairs, the new design's sub-table gives each a column.
    const companyDetails = useCallback(
        (company: CompanySubRow['company']) => [
            { label: t('name'), content: <div>{company.name}</div> },
            { label: t('financial-conditions.company.reg-code'), content: <div>{company.regCode}</div> },
            {
                label: t('is-vat-liable'),
                content: (
                    <div>{company.isVatLiable ? <FormattedMessage id="yes" /> : <FormattedMessage id="no" />}</div>
                ),
            },
            { label: t('financial-conditions.company.vat-number'), content: <div>{company.vatRegCode}</div> },
            {
                label: t('address'),
                content: (
                    <div>
                        {`${company.address}, ${company.cityName}, ${getClassifierText(
                            classifiers?.['COUNTRY'],
                            company.countryCode
                        )}`}
                    </div>
                ),
            },
            {
                label: t('financial-conditions.company.contact-details'),
                width: LONG_WORD_COLUMN,
                content: (
                    <div className="flex flex-wrap">
                        <div className="border-divider border-r-2 pr-1">{company.email || '-'}</div>
                        <div className="pl-1">{company.phone || '-'}</div>
                    </div>
                ),
            },
            {
                label: t('financial-conditions.company.bank-accounts'),
                width: LONG_WORD_COLUMN,
                content: (
                    <div className="flex flex-wrap">
                        <div className="flex flex-col">
                            {(company.bankAccounts || []).map((item) =>
                                canUpdateFinancials ? (
                                    <button
                                        type="button"
                                        className="entity-link text-link! w-fit cursor-pointer text-left hover:underline"
                                        key={item.id}
                                        onClick={() => openCompanyIbanEditModal(company.id, 'update', item)}
                                    >
                                        {item.iban}
                                    </button>
                                ) : (
                                    <div key={item.id}>{item.iban}</div>
                                )
                            )}
                            <PermissionButton
                                permissions={['UPDATE.FINANCIAL_CONDITIONS']}
                                variant="outlined"
                                size="extraSmall"
                                onClick={() => openCompanyIbanEditModal(company.id, 'add')}
                                className="mt-4"
                            >
                                <FormattedMessage id="financial-conditions.company.add-bank-account" />
                            </PermissionButton>
                        </div>
                    </div>
                ),
            },
            {
                label: t('financial-conditions.company.custom-date-format'),
                content: <div>{company.customDateFormat || '-'}</div>,
            },
        ],
        [canUpdateFinancials, classifiers, openCompanyIbanEditModal]
    );

    const innerColumns = useMemo<DataTableColumnDef<CompanySubRow>[]>(
        () => [
            {
                header: '',
                accessorKey: 'company',
                enableSorting: false,
                cell: ({ cell }) => {
                    const { company } = cell.row.original;
                    if (!company) {
                        return <div>{t('financial-conditions.company.contact-details')}</div>;
                    }
                    const details = companyDetails(company);
                    return (
                        <div className="*:not-last:border-divider *:not-first:pt-4 *:not-last:border-b *:not-last:pb-4">
                            {[0, 2, 4, 6].map((first) => (
                                <div key={first} className="flex flex-wrap">
                                    {details.slice(first, first + 2).map(({ label, content }) => (
                                        <div key={label} className="flex-1">
                                            <p className="text-body1 text-fg-secondary">{label}</p>
                                            {content}
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    );
                },
            },
        ],
        [companyDetails]
    );

    const renderExpandedContent = useCallback(
        (row: Company): JSX.Element => {
            const company = {
                id: row.id,
                name: row.name,
                address: row.address,
                regCode: row.regCode,
                vatRegCode: row.vatRegCode,
                email: row.email,
                phone: row.phone,
                isDefault: row.isDefault,
                isVatLiable: row.isVatLiable,
                cityName: row.cityName,
                countryCode: row.countryCode,
                bankAccounts: row.bankAccounts,
                customDateFormat: row.customDateFormat,
            };
            // The new design draws the details as the sub-table My Portfolio opens under a group: one row, a column each.
            return isNewDesign ? (
                <NestedTable<CompanySubRow['company']>
                    data={[company]}
                    columns={companyDetails(company).map(({ label, content, width }) => ({
                        id: label,
                        header: label,
                        width,
                        // A longer IBAN or e-mail address than its column fits wraps rather than run into the next one.
                        Cell: () => <div className="wrap-anywhere">{content}</div>,
                    }))}
                    getRowKey={({ id }) => id}
                />
            ) : (
                <SubRowTable<CompanySubRow>
                    columns={innerColumns}
                    skipHeader
                    className="bg-paper"
                    data={[{ company }]}
                />
            );
        },
        [innerColumns, companyDetails, isNewDesign]
    );

    if (isLoadingClassifiers) {
        return <Loader />;
    }

    return (
        <ListTableWithFilters<Company>
            columnConfigId="financial-conditions"
            columns={columns}
            data={data}
            isLoading={isFetching}
            renderExpandedContent={renderExpandedContent}
            pluralTextId="financial-conditions.financial-conditions"
            singularTextId="financial-condition"
            noEntriesId="financial-conditions.no-conditions-found"
            noEntriesBodyId="financial-conditions.empty.body"
            noEntriesIcon={AccountBalanceWalletOutlinedIcon}
            fetchData={fetchData}
            filterConfig={filterConfig}
            rowsPerPage={50}
        />
    );
};

export default FinancialConditionsTable;
